import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  Text,
  useToast,
  Badge,
  ScaleFade,
  Tooltip,
  Skeleton,
  Tag,
  TagLabel,
  TagLeftIcon,
  VStack,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
} from '@chakra-ui/react';
import { COLORS, GRADIENTS, ROUTES } from '../utils/constants';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';
import { fetchGenerationHistory, mapGenerationToViewModel } from '../services/GenerationService';
import RecentHistoryGrid from '../components/RecentHistoryGrid';
import { FaLock, FaImage, FaVideo, FaRobot } from 'react-icons/fa';

// Utility function for downloading images
const downloadImage = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Simulated icons for menu actions
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function Dashboard() {
  const [isVisible, setIsVisible] = useState(false);
  const [dbGenerations, setDbGenerations] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  
  const navigate = useNavigate();
  const toast = useToast();
  
  const { user, isLoggedIn } = useAuth();
  const { balance } = useCredits();
  // Remove dependency on useCreations store
  
  // Mock videos and chat history for demo
  const mockVideos = [
    {id: 'v1', url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4', thumbnail: 'https://via.placeholder.com/300x200?text=Video+Thumbnail', createdAt: new Date()},
    {id: 'v2', url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4', thumbnail: 'https://via.placeholder.com/300x200?text=Video+Thumbnail', createdAt: new Date()}
  ];
  
  const mockChatHistory = [
    {id: 'c1', title: 'Scene Planning', preview: 'I need help planning a sci-fi scene...', messages: 12, createdAt: new Date()},
    {id: 'c2', title: 'Character Design', preview: 'Can you help create a fantasy character...', messages: 8, createdAt: new Date()},
    {id: 'c3', title: 'Prompt Engineering', preview: 'How do I create better prompts for...', messages: 15, createdAt: new Date()}
  ];
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);
  
  // Load generation history from database
  useEffect(() => {
    async function loadGenerationHistory() {
      if (user?.walletAddress) {
        setIsLoadingHistory(true);
        try {
          const result = await fetchGenerationHistory(user.walletAddress, 20, 0);
          if (result.success) {
            const viewModels = result.generations.map(mapGenerationToViewModel);
            setDbGenerations(viewModels);
          } else {
            console.error('Failed to load generation history:', result.error);
          }
        } catch (error) {
          console.error('Error loading generation history:', error);
        } finally {
          setIsLoadingHistory(false);
        }
      }
    }
    
    loadGenerationHistory();
  }, [user?.walletAddress]);
  
  const handleAddMore = (type) => {
    switch(type) {
      case 'image':
        navigate(ROUTES.GENERATE);
        break;
      case 'video':
        navigate(ROUTES.VIDEO);
        break;
      case 'chat':
        navigate(ROUTES.CHAT);
        break;
      default:
        navigate(ROUTES.GENERATE);
    }
  };
  
  const handleDownload = (imageUrl) => {
    downloadImage(imageUrl, `ignita-creation-${Date.now()}.png`);
    toast({
      title: 'Image downloaded',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };
  
  const handleShare = (imageUrl) => {
    // Simulated share functionality
    toast({
      title: 'Share feature',
      description: 'Sharing functionality would be implemented with a real API',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };
  
  const handleDelete = (generationId) => {
    // Update local state (actual deletion would require backend API)
    setDbGenerations(prev => prev.filter(gen => gen.id !== generationId));
    toast({
      title: 'Image deleted',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };
  
  return (
    <Container maxW="6xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }}>
      <ScaleFade in={isVisible} initialScale={0.95}>
        <VStack spacing={{ base: 6, md: 8 }} align="stretch">
          <Flex 
            direction={{ base: 'column', sm: 'row' }}
            justify="space-between" 
            align={{ base: 'stretch', sm: 'center' }}
            gap={4}
          >
            <Box>
              <Flex align="center" mb={2}>
                <Heading 
                  as="h1" 
                  size={{ base: 'lg', md: 'xl' }}
                  color="white"
                  letterSpacing="tight"
                  fontFamily="Space Grotesk"
                >
                  Your Creations
                </Heading>
              </Flex>
              <Text color={COLORS.textSecondary} fontSize={{ base: 'sm', md: 'md' }}>
                View and manage your AI-generated masterpieces
              </Text>
            </Box>
            
            <Flex 
              align="center" 
              gap={{ base: 2, md: 4 }}
              direction={{ base: 'row', sm: 'row' }}
              w={{ base: 'full', sm: 'auto' }}
              justify={{ base: 'space-between', sm: 'flex-end' }}
            >
              <Badge 
                px={{ base: 3, md: 4 }}
                py={{ base: 1.5, md: 2 }}
                borderRadius="md" 
                fontWeight="bold"
                bg="rgba(0, 168, 107, 0.15)"
                border="1px solid"
                borderColor={COLORS.primary}
                color={COLORS.accent}
                className="pulse"
                fontSize={{ base: 'xs', md: 'sm' }}
              >
                Credits: {balance}
              </Badge>
              
              <Button
                onClick={() => handleAddMore('image')}
                className="primary-button"
                leftIcon={<PlusIcon />}
                size={{ base: 'sm', md: 'md' }}
              >
                <Text display={{ base: 'none', sm: 'inline' }}>Generate New</Text>
                <Text display={{ base: 'inline', sm: 'none' }}>New</Text>
              </Button>
            </Flex>
          </Flex>
          
          {/* Coming Soon Section */}
          <Box 
            py={{ base: 12, md: 20 }}
            px={{ base: 4, md: 8 }}
            textAlign="center" 
            bg="rgba(0, 0, 0, 0.2)"
            borderRadius="xl"
            border="1px dashed"
            borderColor={COLORS.primary}
          >
            <VStack spacing={{ base: 4, md: 6 }}>
              <Icon as={FaLock} color={COLORS.primary} boxSize={{ base: 12, md: 16 }} />
              <Heading 
                as="h2" 
                size={{ base: 'lg', md: 'xl' }}
                color="white"
              >
                Dashboard Coming Soon
              </Heading>
              <Text 
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.300" 
                maxW="xl" 
                mx="auto"
                px={{ base: 2, md: 0 }}
              >
                The dashboard is currently under development. Soon you'll be able to manage all your AI creations in one place.
              </Text>
              <Button
                onClick={() => handleAddMore('image')}
                className="primary-button"
                size={{ base: 'md', md: 'lg' }}
                mt={{ base: 2, md: 4 }}
              >
                Try Generate Page
              </Button>
            </VStack>
          </Box>
          
          {/* Generation History */}
          {dbGenerations.length === 0 && !isLoadingHistory ? (
            <Box 
              py={10} 
              textAlign="center" 
              bg="rgba(0, 0, 0, 0.2)"
              borderRadius="xl"
              border="1px dashed"
              borderColor="gray.600"
            >
              <VStack spacing={4}>
                <Text fontSize="lg" color="gray.400">
                  You haven't created any images yet
                </Text>
                <Button
                  onClick={() => handleAddMore('image')}
                  className="primary-button"
                  leftIcon={<PlusIcon />}
                >
                  Generate Your First Image
                </Button>
              </VStack>
            </Box>
          ) : isLoadingHistory ? (
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' }} gap={4}>
              {Array.from({ length: 12 }).map((_, index) => (
                <Skeleton key={index} height="120px" borderRadius="md" />
              ))}
            </Grid>
          ) : (
            <RecentHistoryGrid items={dbGenerations} limit={20} />
          )}
        </VStack>
      </ScaleFade>
    </Container>
  );
}

export default Dashboard;